import type { SortOrderType } from "@App/types/enums";

export enum EventType {
  GEOLOCATION_REQUESTED = "wizard: geolocation - requested",
  GEOLOCATION_GRANTED = "wizard: geolocation - granted",
  GEOLOCATION_DENIED = "wizard: geolocation - denied",
  GEOLOCATION_FAILED = "wizard: geolocation - failed",
  GEOLOCATION_COMPLETED = "wizard: geolocation - completed",
  GENERIC_ERROR = "generic error",
  RESULT_VIEWED = "result: result - viewed",
  BOOKING_NO_SLOTS_SHOWN = "booking: no slots - shown",
  BOOKING_NEAREST_SLOT_GONE_SHOWN = "booking: nearest slot gone - shown",
}

export type DomainEvent =
  | {
      type: EventType.GEOLOCATION_REQUESTED;
      organization: { id: number; slug: string; name: string };
      origin: string;
    }
  | {
      type: EventType.GEOLOCATION_GRANTED;
      organization: { id: number; slug: string; name: string };
      origin: string;
    }
  | {
      type: EventType.GEOLOCATION_DENIED;
      organization: { id: number; slug: string; name: string };
      origin: string;
    }
  | {
      type: EventType.GEOLOCATION_FAILED;
      organization: { id: number; slug: string; name: string };
      origin: string;
      err: Error;
    }
  | {
      type: EventType.GEOLOCATION_COMPLETED;
      organization: { id: number; slug: string; name: string };
      origin: string;
    }
  | { type: EventType.GENERIC_ERROR; err: Error }
  | ResultViewedEvent
  | BookingNoSlotsShownEvent
  | BookingNearestSlotGoneShownEvent;

/**
 * Booking metadata shared by the booking-calendar events. Only ids are
 * captured; slug/name can be resolved from the ids when building insights.
 */
type BookingContext = {
  organization_id: number;
  garage_id: number | string;
  service_id: number | string;
};

/**
 * The booking calendar has no bookable slots in the range the user is
 * looking at, so the "no slots" alert is shown.
 */
export type BookingNoSlotsShownEvent = BookingContext & {
  type: EventType.BOOKING_NO_SLOTS_SHOWN;
};

/**
 * The earliest slot promised to the user in the result list is gone: the
 * earliest slot now available in the calendar is later than the one that
 * was advertised. The "nearest slot gone" alert is shown.
 */
export type BookingNearestSlotGoneShownEvent = BookingContext & {
  type: EventType.BOOKING_NEAREST_SLOT_GONE_SHOWN;
  // slot advertised in the result list (garage.available_days[0])
  promised_slot_at: string;
  // earliest slot actually available in the calendar now
  actual_slot_at: string;
  // how many calendar days later the actual slot is vs. the promised one
  days_later: number;
};

export type ResultViewedEvent = {
  type: EventType.RESULT_VIEWED;
  organization_name?: string;
  organization_id: number;
  origin: string;
  service_partner_count: number;
  sort_method: SortOrderType;
  service_slug: string | null;
  service_id: string | null;
  service_name: string | null;
  service_radius: number;
  vehicle_brand: string | null;
  vehicle_engine: string | null;
  vehicle_model: string | null;
  loading_time: number;
  distance_closest_service_partner?: number;
  earliest_appointment: string | null;
  premium_services: string[];
};
